'use client';

import { trackUser } from '@api/tracking/client';
import { createPresentationSeriesCompatibilityLayer } from '@mentimeter/compatibility/presentation';
import {
  CoreHooksProvider,
  useSubscribeToResults,
  useVoteCodeWithoutGenerating,
} from '@mentimeter/core-hooks';
// eslint-disable-next-line menti-react/forbidden-legacy-series-import
import type { Series } from '@mentimeter/http-clients';
import { closeCookieBanner } from '@mentimeter/onetrust';
import type { SlideDeck } from '@mentimeter/presentation-schema/api-types-overrides';
import { useOnce } from '@mentimeter/react-hooks';
import { Embed, VoteCodeProvider } from '@mentimeter/sfinx';
import {
  formattedExampleVoteCode,
  isExamplePresentation,
} from 'features/core/presentation/shared/examplePresentation';
import { setupRegion } from 'lib/http/region-setup';
import { useRealtime } from 'lib/realtime/useRealtime';
import { setSeriesIdForIdStore } from 'lib/setValuesForIdStore';
import { ConfigProvider } from 'features/core/presentation/shared/config';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Ratio } from '@mentimeter/ragnar-ui/ratio';
import { usePresentationSlideDeckSubscription } from 'features/pub-sub/subscribe/use-presentation-slide-deck-subscription';
import { useActiveQuestionId } from '@mentimeter/core-hooks/use-ids';
import { PresentationContainer } from '../PresentationContainer';

function EmbedWrapper({
  seriesId,
  shouldSubscribe,
  children,
}: {
  seriesId: string;
  shouldSubscribe: boolean;
  children: React.ReactNode;
}) {
  const questionId = useActiveQuestionId();
  const voteCode = useVoteCodeWithoutGenerating(seriesId);
  useSubscribeToResults(seriesId, { shouldSubscribe });
  usePresentationSlideDeckSubscription(seriesId, questionId, shouldSubscribe);

  return (
    <VoteCodeProvider
      value={
        isExamplePresentation(seriesId) ? formattedExampleVoteCode : voteCode
      }
    >
      {children}
    </VoteCodeProvider>
  );
}

interface EntryEmbedProps {
  seriesResponse:
    | { series: Series; slideDeck: SlideDeck | undefined }
    | { series: Series | undefined; slideDeck: SlideDeck };
  activeQuestionId: string;
}

export const EntryEmbed = ({
  seriesResponse,
  activeQuestionId,
}: EntryEmbedProps) => {
  const slideDeckOrSeries = seriesResponse.slideDeck ?? seriesResponse.series;
  const series = createPresentationSeriesCompatibilityLayer(slideDeckOrSeries)!;

  const hasInitialisedRealtime = useRealtime(series.id);

  useOnce(true, () => {
    trackUser({ event: 'Viewed /embed view' });
    closeCookieBanner();
  });

  setupRegion(series.id);
  setSeriesIdForIdStore(series.id);

  return (
    <CoreHooksProvider
      seriesId={series.id}
      device_id="embed"
      shouldSubscribe={hasInitialisedRealtime}
    >
      <EmbedWrapper
        seriesId={series.id}
        shouldSubscribe={hasInitialisedRealtime}
      >
        <ConfigProvider>
          <PresentationContainer
            series={series}
            questionId={activeQuestionId}
            mode="embed"
          >
            <Box
              bg="black"
              flex="1 1 auto"
              width="100%"
              height="100%"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              px={3}
            >
              <Box width={['100%', '57%', '75%']}>
                <Ratio ratio="landscape">
                  <Embed />
                </Ratio>
              </Box>
            </Box>
          </PresentationContainer>
        </ConfigProvider>
      </EmbedWrapper>
    </CoreHooksProvider>
  );
};
